import InlineOfficeModal from "@/components/cases/inline-office-modal";
import BottomBar from "@/components/core/actions/bottombar";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuItemIndicator,
	DropdownMenuLabel,
	DropdownMenuPortal,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { Checkbox } from "@/components/core/forms/checkbox";
import { CheckboxGroup } from "@/components/core/forms/checkbox-group";
import Combobox from "@/components/core/forms/combobox";
import FormCalendar from "@/components/core/forms/form-calendar";
import FormNumber from "@/components/core/forms/form-number";
import FormPhoneNumber from "@/components/core/forms/form-phone-number";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormIcon, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import ImageSelect from "@/components/core/forms/image-select";
import { Input } from "@/components/core/forms/input";
import { InputFile } from "@/components/core/forms/input-file";
import { RadioGroup, RadioGroupItem } from "@/components/core/forms/radio-group";
import Select from "@/components/core/forms/select";
import TagsInput from "@/components/core/forms/tags-input";
import { Textarea } from "@/components/core/forms/textarea";
import { useModal } from "@/components/core/modals/use-modal.ts";
import Pagination from "@/components/core/pagination";
import { Searchbar } from "@/components/core/searchbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import Tooltip from "@/components/core/tooltip";
import SampleModal from "@/components/sample-modal";
import InlineEditorModal from "@/features/new-case-ui/inline-editor-modal";
import {
	IconCheck,
	IconChevronDown,
	IconChevronRight,
	IconCommand,
	IconFile,
	IconFileTypeDoc,
	IconFileTypePdf,
	IconFileTypePng,
	IconLock,
	IconPrinter,
	IconUser,
} from "@tabler/icons-react";
import { Link, createFileRoute } from "@tanstack/react-router";
import clsx from "clsx";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";

export const Route = createFileRoute("/help-guides")({
	component: GuidesPage,
});

function GuidesPage() {
	const modal = useModal();
	const form = useForm({
		defaultValues: {
			id: 1,
			username: "",
			password: "",
			description: "",
			attachment: "",
			selectbox: "",
			combobox: "",
			tagsinput: "",
			datepicker: "",
		},
	});
	const [bookmarksChecked, setBookmarksChecked] = useState(true);
	const [urlsChecked, setUrlsChecked] = useState(false);
	const [person, setPerson] = useState("pedro");

	type FundSource = {
		name: string;
		category: string;
		fund_classification: string;
		updated_at: string; // or `Date` if you want to handle dates directly
	};

	const columns: ColumnDef<FundSource>[] = [
		{
			id: "name",
			label: "Name",
			cell: ({ data, onClick }) => (
				<button type="button" onClick={() => onClick?.(data)} className="font-semibold text-base text-blue-600">
					{data.name}
				</button>
			),
		},
		{
			id: "category",
			label: "Category",
			cell: ({ data }) => data.category,
		},
		{
			id: "fund_classification",
			label: "Fund Classification",
			cell: ({ data }) => data.fund_classification,
		},
		{
			id: "updated_at",
			label: "Updated",
			cell: ({ data }) => formatDate(data.updated_at),
		},
	];

	const fundSources: FundSource[] = [
		{
			name: "Education Fund",
			category: "Education",
			fund_classification: "Government",
			updated_at: "2025-01-10",
		},
		{
			name: "Health Fund",
			category: "Healthcare",
			fund_classification: "NGO",
			updated_at: "2025-01-08",
		},
		{
			name: "Infrastructure Fund",
			category: "Development",
			fund_classification: "Private",
			updated_at: "2025-01-05",
		},
	];

	const formatDate = (date: string): string => {
		const options: Intl.DateTimeFormatOptions = {
			year: "numeric",
			month: "short",
			day: "numeric",
		};
		return new Date(date).toLocaleDateString(undefined, options);
	};

	const handleEdit = () => {
		alert("Edit Mode");
	};

	const handleArchive = () => {
		alert("Archive Mode");
	};

	const [isBottomBarVisible, setBottomBarVisible] = useState(false);

	const toggleBottomBar = () => {
		setBottomBarVisible((prev) => !prev);
	};

	const fileData = [
		{ name: "document1.pdf", type: "pdf" },
		{ name: "presentation2.doc", type: "doc" },
		{ name: "photo3.png", type: "png" },
		{ name: "longfilenameexample4.pdf", type: "pdf" },
		{ name: "notes5.doc", type: "doc" },
		{ name: "picture6.jpg", type: "png" },
		{ name: "report7.pdf", type: "pdf" },
		{ name: "summary8.doc", type: "doc" },
		{ name: "landscape9.png", type: "png" },
		{ name: "assignment10.pdf", type: "pdf" },
	];

	const getFileIcon = (type) => {
		switch (type) {
			case "pdf":
				return <IconFileTypePdf size={18} stroke={1.5} />;
			case "doc":
				return <IconFileTypeDoc size={18} stroke={1.5} />;
			case "png":
				return <IconFileTypePng size={18} stroke={1.5} />;
			default:
				return <IconFile size={18} stroke={1.5} />;
		}
	};

	const truncateFileName = (fileName, maxLength = 15) => {
		if (fileName.length > maxLength) {
			const extension = fileName.split(".").pop();
			const truncated = fileName.substring(0, maxLength - extension.length - 3);
			return `${truncated}...${extension}`;
		}
		return fileName;
	};

	return (
		<div className="flex flex-col p-12 gap-8 bg-background">
			<Form {...form}>
				<form>
					<div className="flex flex-col w-full gap-6">
						<div className="flex flex-row w-full">
							<Tooltip content="Download" side="top">
								<Button size="icon">
									<IconPrinter size={18} stroke={1.5} />
								</Button>
							</Tooltip>
						</div>

						<div className="flex flex-row flex-wrap">
							{fileData.map((file, index) => (
								<Link
									to="/"
									key={index}
									className="inline-flex flex-row items-center gap-1 mr-1 mb-1 p-1 pr-2 w-auto rounded-md whitespace-nowrap bg-blue-50 hover:bg-blue-100/70"
								>
									<span
										className={clsx(
											"inline-flex justify-center items-center w-[24px] h-[24px] rounded text-white",
											file.type === "pdf" && "bg-red-500",
											file.type === "doc" && "bg-blue-500",
											file.type === "png" && "bg-slate-500",
										)}
									>
										{getFileIcon(file.type)}
									</span>
									<span className="text-base font-semibold text-blue-600">{truncateFileName(file.name)}</span>
								</Link>
							))}
						</div>

						<div className="flex flex-row w-full gap-2">
							<Button type="button" onClick={toggleBottomBar}>
								Fire Action Bar
							</Button>

							<BottomBar isVisible={isBottomBarVisible} onClose={() => setBottomBarVisible(false)}>
								<p>This is the content of the BottomBar!</p>
							</BottomBar>
						</div>

						<div className="flex flex-row w-full gap-2">
							<Button
								type="button"
								onClick={() => {
									modal.openModal({
										allowClose: false,
										title: "Yo, Dis Modal Defolt",
										description: "Yes, this is for the description, Please make it short",
										size: "full",
										showHeader: false,
										showFooter: false,
										component: <InlineEditorModal />,
									});
								}}
							>
								Inline Text Editor
							</Button>
							<Button
								type="button"
								onClick={() => {
									modal.openModal({
										allowClose: false,
										title: "Yo, Dis Modal Defolt",
										description: "Yes, this is for the description, Please make it short",
										size: "full",
										showHeader: false,
										showFooter: false,
										component: <InlineOfficeModal />,
									});
								}}
							>
								Inline Office
							</Button>
							<Button
								type="button"
								onClick={() => {
									modal.openModal({
										allowClose: false,
										title: "Yo, Dis Modal Defolt",
										description: "Yes, this is for the description, Please make it short",
										size: "md",
										showHeader: true,
										showFooter: false,
										component: <SampleModal />,
									});
								}}
							>
								Default Modal
							</Button>
							<Button
								type="button"
								onClick={() => {
									modal.openModal({
										allowClose: false,
										title: "Yo, Dis Modal Defolt",
										description: "Yes, this is for the description, Please make it short",
										size: "sm",
										showHeader: true,
										showFooter: false,
										component: <SampleModal />,
									});
								}}
							>
								Small Modal
							</Button>
							<Button
								type="button"
								onClick={() => {
									modal.openModal({
										allowClose: false,
										title: "Yo, Dis Modal Defolt",
										description: "Yes, this is for the description, Please make it short",
										size: "lg",
										showHeader: true,
										showFooter: false,
										component: <SampleModal />,
									});
								}}
							>
								Large Modal
							</Button>
							<Button
								type="button"
								onClick={() => {
									modal.openModal({
										allowClose: false,
										title: "Yo, Dis Modal Defolt",
										description: "Yes, this is for the description, Please make it short",
										size: "xl",
										showHeader: true,
										showFooter: false,
										component: <SampleModal />,
									});
								}}
							>
								Extra Large Modal
							</Button>
							<Button
								type="button"
								onClick={() => {
									modal.openModal({
										type: "confirm",
										variant: "green",
										title: "You sure?",
										description: "Are you sure you want to do this destructive action? You cannot undo this action once confirmed.",
										onConfirm: () => alert("Confirmed!"),
									});
								}}
							>
								Fire Confirm Modal
							</Button>
						</div>

						<div className="flex flex-row items-center w-full gap-2">
							<DropdownMenu modal={false}>
								<DropdownMenuTrigger asChild>
									<Button>
										<span className="">Dropdown</span>
										<IconChevronDown size={18} stroke={2} />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start" sideOffset={8}>
									<DropdownMenuGroup>
										<DropdownMenuItem>
											<div className="flex flex-row items-center w-full py-1 px-2">
												Some Action
												<DropdownMenuShortcut>
													<IconCommand size={18} stroke={1.5} />
												</DropdownMenuShortcut>
											</div>
										</DropdownMenuItem>
										<DropdownMenuItem>
											<div className="flex flex-row items-center w-full py-1 px-2">
												Another Action
												<DropdownMenuShortcut>
													<IconCommand size={18} stroke={1.5} />
												</DropdownMenuShortcut>
											</div>
										</DropdownMenuItem>
										<DropdownMenuItem>
											<div className="flex flex-row items-center w-full py-1 px-2">
												Some Other Action
												<DropdownMenuShortcut>
													<IconCommand size={18} stroke={1.5} />
												</DropdownMenuShortcut>
											</div>
										</DropdownMenuItem>
									</DropdownMenuGroup>
									<DropdownMenuSub>
										<DropdownMenuSubTrigger>
											<div className="flex flex-row items-center w-full py-1 px-2">
												More Action
												<DropdownMenuShortcut>
													<IconChevronRight size={18} stroke={1.5} />
												</DropdownMenuShortcut>
											</div>
										</DropdownMenuSubTrigger>
										<DropdownMenuPortal>
											<DropdownMenuSubContent sideOffset={6} alignOffset={-4}>
												<DropdownMenuItem>
													<div className="flex flex-row items-center w-full py-1 px-2">
														Some Action
														<DropdownMenuShortcut>
															<IconCommand size={18} stroke={1.5} />
														</DropdownMenuShortcut>
													</div>
												</DropdownMenuItem>
												<DropdownMenuItem>
													<div className="flex flex-row items-center w-full py-1 px-2">
														Another Action
														<DropdownMenuShortcut>
															<IconCommand size={18} stroke={1.5} />
														</DropdownMenuShortcut>
													</div>
												</DropdownMenuItem>
												<DropdownMenuItem>
													<div className="flex flex-row items-center w-full py-1 px-2">
														Some Other Action
														<DropdownMenuShortcut>
															<IconCommand size={18} stroke={1.5} />
														</DropdownMenuShortcut>
													</div>
												</DropdownMenuItem>
												<DropdownMenuItem disabled>
													<div className="flex flex-row items-center w-full py-1 px-2">
														Disabled Action
														<DropdownMenuShortcut>
															<IconCommand size={18} stroke={1.5} />
														</DropdownMenuShortcut>
													</div>
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem dangerZone>
													<div className="flex flex-row items-center w-full py-1 px-2">
														Dangerous Action
														<DropdownMenuShortcut>
															<IconCommand size={18} stroke={1.5} />
														</DropdownMenuShortcut>
													</div>
												</DropdownMenuItem>
											</DropdownMenuSubContent>
										</DropdownMenuPortal>
									</DropdownMenuSub>
									<DropdownMenuSeparator />
									<DropdownMenuGroup>
										<DropdownMenuLabel>Checkbox</DropdownMenuLabel>
										<DropdownMenuCheckboxItem checked={bookmarksChecked} onCheckedChange={setBookmarksChecked}>
											Show Bookmarks
										</DropdownMenuCheckboxItem>
										<DropdownMenuCheckboxItem checked={urlsChecked} onCheckedChange={setUrlsChecked}>
											Show URL
										</DropdownMenuCheckboxItem>
									</DropdownMenuGroup>
									<DropdownMenuSeparator />
									<DropdownMenuGroup>
										<DropdownMenuLabel>Radio</DropdownMenuLabel>
										<DropdownMenuRadioGroup value={person} onValueChange={setPerson}>
											<DropdownMenuRadioItem className="DropdownMenuRadioItem" value="pedro">
												Pedro Duarte
											</DropdownMenuRadioItem>
											<DropdownMenuRadioItem className="DropdownMenuRadioItem" value="colm">
												Colm Tuite
											</DropdownMenuRadioItem>
										</DropdownMenuRadioGroup>
									</DropdownMenuGroup>
									<DropdownMenuSeparator />
									<DropdownMenuItem disabled>
										<div className="flex flex-row items-center w-full py-1 px-2">
											Disabled Action
											<DropdownMenuShortcut>
												<IconCommand size={18} stroke={1.5} />
											</DropdownMenuShortcut>
										</div>
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem dangerZone>
										<div className="flex flex-row items-center w-full py-1 px-2">
											Dangerous Action
											<DropdownMenuShortcut>
												<IconCommand size={18} stroke={1.5} />
											</DropdownMenuShortcut>
										</div>
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>

						<div className="flex flex-col w-full">
							<Tabs orientation="vertical" defaultValue="account">
								<TabsList>
									<TabsTrigger value="account">Account</TabsTrigger>
									<TabsTrigger value="security">Security</TabsTrigger>
									<TabsTrigger value="sessions">Sessions</TabsTrigger>
								</TabsList>
								<TabsContent value="account">
									<span>Account</span>
								</TabsContent>
								<TabsContent value="security">
									<span>Security</span>
								</TabsContent>
								<TabsContent value="sessions">
									<span>Sessions</span>
								</TabsContent>
							</Tabs>
						</div>

						<div className="flex flex-col w-full">
							<Tabs orientation="horizontal" defaultValue="account">
								<TabsList>
									<TabsTrigger value="account">Account</TabsTrigger>
									<TabsTrigger value="security">Security</TabsTrigger>
									<TabsTrigger value="sessions">Sessions</TabsTrigger>
								</TabsList>
								<TabsContent value="account">
									<span>Account</span>
								</TabsContent>
								<TabsContent value="security">
									<span>Security</span>
								</TabsContent>
								<TabsContent value="sessions">
									<span>Sessions</span>
								</TabsContent>
							</Tabs>
						</div>

						<div className="flex flex-col w-full border border-border h-[600px]">
							<PanelGroup className="flex flex-row grow w-full " direction="horizontal">
								<Panel defaultSize={50} minSize={10} className="flex flex-col shrink-0 grow ">
									<span className="">Ang Nag-akusa</span>
								</Panel>

								<PanelResizeHandle className="group flex justify-center items-center w-[12px] border-r border-l border-border/50 bg-white">
									<div className="inline-flex w-[4px] h-[40px] rounded-sm bg-foreground/50 group-hover:bg-foreground/90" />
								</PanelResizeHandle>

								<Panel defaultSize={50} minSize={10} className="flex flex-col shrink grow w-full">
									<span className="">Ang Akusado</span>
								</Panel>

								<PanelResizeHandle className="group flex justify-center items-center w-[12px] border-r border-l border-border/50 bg-white">
									<div className="inline-flex w-[4px] h-[40px] rounded-sm bg-foreground/50 group-hover:bg-foreground/90" />
								</PanelResizeHandle>

								<Panel defaultSize={50} minSize={10} className="flex flex-col shrink grow w-full">
									<span className="">Ang Akusado</span>
								</Panel>
							</PanelGroup>
						</div>

						<div className="flex flex-col w-full">
							<Searchbar />
							<DataTable data={fundSources} columns={columns} onEdit={handleEdit} onRowClick={handleEdit} onDelete={handleArchive} />
							<Pagination
								onClick={() => {
									alert("Wow");
								}}
								initialPage={1}
								pageCount={4}
							/>
						</div>

						<div className="grid grid-cols-12 w-full gap-2">
							<div className="col-span-12 flex flex-col w-full">
								<Controller
									render={({ field }) => (
										<ImageSelect
											initials="AB"
											avatar="https://example.com/avatar_thumbnail.jpg"
											onImageRemove={async () => {
												field.onChange("");
											}}
											onCrop={(avatar) => {
												field.onChange(avatar);
											}}
										/>
									)}
									control={form.control}
									name="avatar"
								/>
							</div>
							<div className="col-span-12 flex flex-col w-full">
								<FormField
									control={form.control}
									name="username"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required error>
													Username or Email
												</FormLabel>
												<FormDescription>Some form input requires description, this is the right place for that.</FormDescription>
											</div>
											<FormGroup iconPosition="left">
												<FormIcon>
													<IconUser size={18} stroke={1.5} />
												</FormIcon>
												<Input autoComplete="off" placeholder="Username or Email" {...field} />
											</FormGroup>
											<FormMessage>Yes, error message is here aside from alert box</FormMessage>
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-12 flex flex-col w-full">
								<FormField
									control={form.control}
									name="password"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Password</FormLabel>
												<FormDescription>Some form input requires description, this is the right place for that.</FormDescription>
											</div>
											<FormGroup iconPosition="left">
												<Input type="password" placeholder="Password" {...field} />
												<FormIcon>
													<IconLock size={18} stroke={1.5} />
												</FormIcon>
											</FormGroup>
											<FormMessage>Yes, error message is here aside from alert box</FormMessage>
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-12 flex flex-col w-full">
								<FormField
									control={form.control}
									name="description"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Password</FormLabel>
												<FormDescription>Some form input requires description, this is the right place for that.</FormDescription>
											</div>
											<FormGroup>
												<Textarea {...field} />
											</FormGroup>
											<FormMessage>Yes, error message is here aside from alert box</FormMessage>
										</FormItem>
									)}
								/>
							</div>
							<div className="flex flex-col w-full col-span-12">
								<FormField
									control={form.control}
									name="attachment"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Attachment</FormLabel>
												<FormDescription>Some form input requires description, this is the right place for that.</FormDescription>
											</div>
											<FormGroup>
												<InputFile {...field} />
											</FormGroup>
											<FormMessage>Yes, error message is here aside from alert box</FormMessage>
										</FormItem>
									)}
								/>
							</div>
							<div className="flex flex-col w-full col-span-12">
								<FormField
									control={form.control}
									name="selectbox"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Selectbox</FormLabel>
												<FormDescription>Some form input requires description, this is the right place for that.</FormDescription>
											</div>
											<FormGroup>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
													options={[
														{
															value: "Option 1",
															label: "Option 1",
														},
														{
															value: "Option 2",
															label: "Option 2",
														},
														{
															value: "Option 3",
															label: "Option 3",
														},
													]}
												/>
											</FormGroup>
											<FormMessage>Yes, error message is here aside from alert box</FormMessage>
										</FormItem>
									)}
								/>
							</div>
							<div className="flex flex-col w-full col-span-12">
								<FormField
									control={form.control}
									name="combobox"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Combobox</FormLabel>
												<FormDescription>Some form input requires description, this is the right place for that.</FormDescription>
											</div>
											<FormGroup>
												<Combobox
													maxHeight={240}
													onValueChange={(value) => field.onChange(value)}
													defaultValue={field.value}
													loadOptions={async (value: string) => {
														return [
															{ id: "1", name: "Option 1" },
															{ id: "2", name: "Option 2" },
															{ id: "3", name: "Option 3" },
														]
															.filter((item) => item.name.toLowerCase().includes(value.toLowerCase()))
															.map((item) => ({
																label: item.name,
																value: item.id,
															}));
													}}
												/>
											</FormGroup>
											<FormMessage>Yes, error message is here aside from alert box</FormMessage>
										</FormItem>
									)}
								/>
							</div>
							<div className="flex flex-col w-full col-span-12">
								<FormField
									control={form.control}
									name="tagsinput"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Tags Input</FormLabel>
												<FormDescription>Some form input requires description, this is the right place for that.</FormDescription>
											</div>
											<FormGroup>
												<TagsInput
													onValueChange={field.onChange}
													defaultValue={[
														{ label: "Option 1", value: "Option 1" },
														{ label: "Option 2", value: "Option 2" },
														{ label: "Option 3", value: "Option 3" },
													]}
												/>
											</FormGroup>
											<FormMessage>Yes, error message is here aside from alert box</FormMessage>
										</FormItem>
									)}
								/>
							</div>
							<div className="flex flex-col w-full col-span-12">
								<FormField
									control={form.control}
									name="datepicker"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Datepicker</FormLabel>
												<FormDescription>Some form input requires description, this is the right place for that.</FormDescription>
											</div>
											<FormGroup>
												<FormCalendar onChange={(date) => console.log(date)} id="calendar" showIcon={true} showTimePicker />
											</FormGroup>
											<FormMessage>Yes, error message is here aside from alert box</FormMessage>
										</FormItem>
									)}
								/>
							</div>
							<div className="flex flex-col w-full col-span-12">
								<FormField
									control={form.control}
									name="radiogroup"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Radio Group</FormLabel>
												<FormDescription>Some form input requires description, this is the right place for that.</FormDescription>
											</div>
											<RadioGroup onValueChange={field.onChange} defaultValue={field.value}>
												<FormItem>
													<div className="flex flex-row items-center w-full gap-2">
														<FormControl>
															<RadioGroupItem value="rb1" />
														</FormControl>
														<FormLabel>
															<div className="flex flex-col w-full">
																<span className="text-base text-foreground">Automatically Generate a password</span>
																<span className="text-sm text-muted-foreground">You'll be able to view and copy the password in the next step</span>
															</div>
														</FormLabel>
													</div>
												</FormItem>
												<FormItem>
													<div className="flex flex-row items-center w-full gap-2">
														<FormControl>
															<RadioGroupItem value="rb2" />
														</FormControl>
														<FormLabel>
															<div className="flex flex-col w-full">
																<span className="text-base text-foreground">Create new password</span>
																<span className="text-sm text-muted-foreground">You'll be able to view and copy the password in the next step</span>
															</div>
														</FormLabel>
													</div>
												</FormItem>
											</RadioGroup>
											<FormMessage>Yes, error message is here aside from alert box</FormMessage>
										</FormItem>
									)}
								/>
							</div>
							<div className="flex flex-col w-full col-span-12">
								<FormField
									control={form.control}
									name="checkboxgroup"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Checkbox Group</FormLabel>
												<FormDescription>Some form input requires description, this is the right place for that.</FormDescription>
											</div>
											<CheckboxGroup>
												<FormItem>
													<FormGroup>
														<div className="flex flex-row w-full gap-2">
															<FormControl>
																<Checkbox onCheckedChange={field.onChange} />
															</FormControl>
															<FormLabel>
																<div className="flex flex-col w-full">
																	<span className="text-base text-foreground">Redirect after transfer</span>
																	<span className="text-sm text-muted-foreground">This will redirect you to the activity you are transferring to.</span>
																</div>
															</FormLabel>
														</div>
													</FormGroup>
													<FormMessage />
												</FormItem>
												<FormItem>
													<FormGroup>
														<div className="flex flex-row w-full gap-2">
															<FormControl>
																<Checkbox onCheckedChange={field.onChange} />
															</FormControl>
															<FormLabel>
																<div className="flex flex-col w-full">
																	<span className="text-base text-foreground">Redirect after transfer</span>
																	<span className="text-sm text-muted-foreground">This will redirect you to the activity you are transferring to.</span>
																</div>
															</FormLabel>
														</div>
													</FormGroup>
													<FormMessage />
												</FormItem>
											</CheckboxGroup>
											<FormMessage>Yes, error message is here aside from alert box</FormMessage>
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>
				</form>
			</Form>

			<div className="flex flex-row items-center w-full gap-2">
				<Button className="" size="xs">
					Button
				</Button>
				<Button className="" size="sm">
					Button
				</Button>
				<Button className="">Button</Button>
				<Button className="" size="lg">
					Button
				</Button>
				<Button className="" size="icon">
					B
				</Button>
			</div>
			<div className="flex flex-row items-center w-full gap-2">
				<Button size="xs" loading>
					Button
				</Button>
				<Button size="sm" loading>
					Button
				</Button>
				<Button loading>Button</Button>
				<Button size="lg" loading>
					Button
				</Button>
				<Button size="icon" loading>
					B
				</Button>
			</div>
			<div className="flex flex-row items-center w-full gap-2">
				<Button className="" variant="ghost" size="xs">
					Button
				</Button>
				<Button className="" variant="ghost" size="sm">
					Button
				</Button>
				<Button className="" variant="ghost">
					Button
				</Button>
				<Button className="" variant="ghost" size="lg">
					Button
				</Button>
				<Button className="" variant="ghost" size="icon">
					B
				</Button>
			</div>
			<div className="flex flex-row items-center w-full gap-2">
				<Button className="" variant="secondary" size="xs">
					Button
				</Button>
				<Button className="" variant="secondary" size="sm">
					Button
				</Button>
				<Button className="" variant="secondary">
					Button
				</Button>
				<Button className="" variant="secondary" size="lg">
					Button
				</Button>
				<Button className="" variant="secondary" size="icon">
					B
				</Button>
			</div>
			<div className="flex flex-row items-center w-full gap-2">
				<Button className="" variant="danger" size="xs">
					Button
				</Button>
				<Button className="" variant="danger" size="sm">
					Button
				</Button>
				<Button className="" variant="danger">
					Button
				</Button>
				<Button className="" variant="danger" size="lg">
					Button
				</Button>
				<Button className="" variant="danger" size="icon">
					B
				</Button>
			</div>
			<div className="flex flex-row items-center w-full gap-2">
				<Button className="" variant="outline" size="xs">
					Button
				</Button>
				<Button className="" variant="outline" size="sm">
					Button
				</Button>
				<Button className="" variant="outline">
					Button
				</Button>
				<Button className="" variant="outline" size="lg">
					Button
				</Button>
				<Button className="" variant="outline" size="icon">
					B
				</Button>
			</div>
			<div className="flex flex-row items-center w-full gap-2">
				<Button className="" variant="link" size="xs">
					Button
				</Button>
				<Button className="" variant="link" size="sm">
					Button
				</Button>
				<Button className="" variant="link">
					Button
				</Button>
				<Button className="" variant="link" size="lg">
					Button
				</Button>
				<Button className="" variant="link" size="icon">
					B
				</Button>
			</div>
		</div>
	);
}
